import React, { useEffect, useState } from 'react';
import {
    Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton,
    LinearProgress, MenuItem, Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField,
    Tooltip, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import { useDropzone } from 'react-dropzone';
import { formatPhone, personName } from '../../voice/callFormat';
import {
    WHO_LABEL, candidateNotes, dayName, dayPhrase, dayUploadHint, formatListDate,
} from './missedCallFormat';
import useScreenshotImport from './useScreenshotImport';
import DuplicateChoice from './DuplicateChoice';
import DayChoice from './DayChoice';

/**
 * Screenshots → rows (docs/MISSED_CALLS_PLAN.md §3.1–3.2, §6.5):
 *   1. "taken today?"  2. pick or drop the screenshots  3. upload  4. wait while they are read (socket + poll)
 *   5. review — fix numbers, times and counts, add a missed one, untick what shouldn't be saved — then 저장.
 * Closing after the upload cancels the import (its screenshots are deleted). The flow itself is
 * `useScreenshotImport`, shared with the phone page (MissedCallsUpload).
 */
const ImportDialog = ({ open, onClose, config, onSaved }) => {
    const theme = useTheme();
    const phone = useMediaQuery(theme.breakpoints.down('sm'));
    const [zoom, setZoom] = useState(null);
    const flow = useScreenshotImport({ config, onGone: onClose });
    const { step, setStep, files, addFiles, removeFile, reset, progress, imp, review, setRow, addRow, busy, errors, maxFiles, maxMb } = flow;

    useEffect(() => { if (open) reset(); }, [open, reset]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        accept: { 'image/*': [] },
        multiple: true,
        maxSize: maxMb * 1024 * 1024,
        onDrop: (accepted, rejected) => addFiles(accepted, rejected.length),
    });

    // Rows whose number is already on today's list and that nobody has answered yet — 저장 waits for these.
    const undecided = review.filter((r) => r.include && r.candidate?.merge_into && !r.on_duplicate).length;

    const discardAndClose = async () => {
        await flow.discard();
        onClose();
    };

    const save = async () => {
        const out = await flow.save();
        if (out) {
            onSaved(out);
            onClose();
        }
    };

    // Once screenshots are going up there is work to lose: a stray click outside, or Esc, does nothing. X and 취소 still cancel.
    const onDialogClose = (event, reason) => {
        if (busy) return;
        if ((reason === 'backdropClick' || reason === 'escapeKeyDown') && !['confirm', 'pick'].includes(step)) return;
        discardAndClose();
    };

    const today = formatListDate(config?.today);
    // Several days in one upload (plan §3.9): the review has to say which list each row goes on.
    const multiDay = (flow.importDays || []).length > 1;
    const dayWord = dayName(flow.importDate, config?.today);   // 오늘, or "9/14 (일)" for a past day's list
    return (
        <Dialog open={open} onClose={onDialogClose} fullWidth maxWidth={step === 'review' ? 'lg' : 'sm'} fullScreen={phone} disableEnforceFocus>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ flex: 1 }}>스크린샷으로 부재중 추가</Box>
                <IconButton size="small" onClick={discardAndClose} disabled={busy}><CloseIcon fontSize="small" /></IconButton>
            </DialogTitle>

            <DialogContent dividers>
                {step === 'confirm' && (
                    <Box sx={{ py: 2, textAlign: 'center' }}>
                        <Typography variant="h6" sx={{ mb: 1 }}>어느 날의 부재중을 올리나요?</Typography>
                        {config?.today && <DayChoice today={config.today} pastDays={config.past_days} value={flow.listDates} onChange={flow.setListDates} multiple />}
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, mb: 2.5 }}>
                            {dayUploadHint(flow.importDays, config?.today)}
                        </Typography>
                        <Typography variant="h5" sx={{ mb: 1 }}>이 스크린샷은 오늘{today ? ` ${today}` : ''} 찍은 건가요?</Typography>
                    </Box>
                )}

                {step === 'pick' && (
                    <>
                        <Box
                            {...getRootProps()}
                            sx={{
                                border: '2px dashed', borderColor: isDragActive ? 'primary.main' : 'divider', borderRadius: 2,
                                p: 3, textAlign: 'center', cursor: 'pointer',
                            }}
                        >
                            <input {...getInputProps()} />
                            <CloudUploadOutlinedIcon fontSize="large" />
                            <Typography>스크린샷을 끌어다 놓거나 눌러서 고르세요</Typography>
                            <Typography variant="caption" color="text.secondary">최대 {maxFiles}장 · 장당 {maxMb}MB</Typography>
                        </Box>
                        {files.length > 0 && (
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.5 }}>
                                {files.map((f, i) => (
                                    <Box key={f.url} sx={{ position: 'relative' }}>
                                        <img src={f.url} alt={`스크린샷 ${i + 1}`} style={{ height: 120, borderRadius: 6, display: 'block' }} />
                                        <IconButton
                                            size="small"
                                            onClick={() => removeFile(f.url)}
                                            sx={{ position: 'absolute', top: 2, right: 2, bgcolor: 'background.paper', p: 0.25 }}
                                        >
                                            <CloseIcon sx={{ fontSize: 14 }} />
                                        </IconButton>
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </>
                )}

                {step === 'upload' && (
                    <Box sx={{ py: 3 }}>
                        <Typography sx={{ mb: 1 }}>올리는 중…</Typography>
                        <LinearProgress variant="determinate" value={Math.round(progress * 100)} />
                    </Box>
                )}

                {step === 'reading' && (
                    <Box sx={{ py: 3, textAlign: 'center' }}>
                        <CircularProgress size={28} />
                        <Typography sx={{ mt: 1 }}>스크린샷을 읽는 중… (보통 1분 안에 끝납니다)</Typography>
                    </Box>
                )}

                {step === 'failed' && (
                    <Alert severity="error">{imp?.error_message || '스크린샷을 읽지 못했습니다.'}</Alert>
                )}

                {step === 'review' && (
                    <>
                        {flow.pastDay && (
                            <Alert severity="info" sx={{ mb: 1 }}>
                                {multiDay
                                    ? `${flow.importDays.map(formatListDate).join(', ')} 목록에 나누어 저장합니다. 지난 날짜의 행은`
                                    : `${formatListDate(flow.importDate)} 목록에 저장합니다.`} 부재 시간을 비워 두면 "시간 모름"으로 저장됩니다.
                            </Alert>
                        )}
                        {(imp?.warnings || []).map((w) => (
                            <Alert key={`${w.reason}-${w.index ?? ''}`} severity={w.reason === 'compare_failed' ? 'info' : 'warning'} sx={{ mb: 1 }}>
                                {w.reason === 'not_call_history'
                                    ? `스크린샷 ${w.index + 1}은 최근 통화 화면이 아닌 것 같아 건너뛰었습니다.`
                                    : '두 번째 판독이 일부 실패해서 비교하지 않았습니다.'}
                            </Alert>
                        ))}
                        {(imp?.screenshots || []).length > 0 && (
                            <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', mb: 1.5, pb: 0.5 }}>
                                {imp.screenshots.map((s) => (
                                    <Tooltip key={s.index} title="크게 보기">
                                        <img
                                            src={s.url}
                                            alt={`스크린샷 ${s.index + 1}`}
                                            onClick={() => setZoom(s.url)}
                                            style={{ height: 140, borderRadius: 6, cursor: 'zoom-in', flex: '0 0 auto' }}
                                        />
                                    </Tooltip>
                                ))}
                            </Box>
                        )}
                        <TableContainer sx={{ maxHeight: phone ? 'none' : '55vh' }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell padding="checkbox">저장</TableCell>
                                        <TableCell sx={{ minWidth: 150 }}>번호</TableCell>
                                        {multiDay && <TableCell sx={{ minWidth: 92 }}>날짜</TableCell>}
                                        <TableCell>화면 표시</TableCell>
                                        <TableCell sx={{ minWidth: 110 }}>부재 시간</TableCell>
                                        <TableCell sx={{ minWidth: 70 }}>횟수</TableCell>
                                        <TableCell>Who</TableCell>
                                        <TableCell sx={{ minWidth: 250 }}>{multiDay ? '같은 날 같은 번호' : `${dayWord} 같은 번호`}</TableCell>
                                        <TableCell sx={{ minWidth: 180 }}>메모</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {review.map((r, i) => {
                                        const c = r.candidate;
                                        const compare = c?.compare && !c.compare.missing ? c.compare : null;
                                        return (
                                            <TableRow key={r.key} sx={{ opacity: r.include ? 1 : 0.5 }}>
                                                <TableCell padding="checkbox">
                                                    <Checkbox checked={r.include} onChange={(e) => setRow(r.key, { include: e.target.checked })} />
                                                </TableCell>
                                                <TableCell>
                                                    <TextField
                                                        value={r.phone_number}
                                                        onChange={(e) => setRow(r.key, { phone_number: e.target.value })}
                                                        size="small"
                                                        placeholder="(201) 555-1234"
                                                        error={Boolean(errors[i]) && errors[i].startsWith('번호')}
                                                        inputProps={{ inputMode: 'tel' }}
                                                    />
                                                </TableCell>
                                                {multiDay && (
                                                    <TableCell>
                                                        <Select
                                                            size="small"
                                                            value={flow.importDays.includes(r.list_date) ? r.list_date : ''}
                                                            onChange={(e) => setRow(r.key, { list_date: e.target.value })}
                                                            displayEmpty
                                                            error={Boolean(errors[i]) && errors[i].startsWith('날짜')}
                                                            sx={{ minWidth: 88 }}
                                                        >
                                                            {/* A 다른 날 candidate arrives with no day. It is untickable-but-shown, so the
                                                                empty choice stays selectable rather than silently defaulting. */}
                                                            <MenuItem value=""><em>고르세요</em></MenuItem>
                                                            {flow.importDays.map((d) => (
                                                                <MenuItem key={d} value={d}>{formatListDate(d)}</MenuItem>
                                                            ))}
                                                        </Select>
                                                    </TableCell>
                                                )}
                                                <TableCell><Typography variant="body2">{r.raw_label}</Typography></TableCell>
                                                <TableCell>
                                                    <TextField
                                                        type="time"
                                                        value={r.missed_time}
                                                        onChange={(e) => setRow(r.key, { missed_time: e.target.value })}
                                                        size="small"
                                                        error={Boolean(errors[i]) && errors[i].startsWith('시간')}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <TextField
                                                        type="number"
                                                        value={r.missed_count}
                                                        onChange={(e) => setRow(r.key, { missed_count: e.target.value })}
                                                        size="small"
                                                        inputProps={{ min: 1, max: 99, style: { width: 44 } }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    {(c?.matches || []).length === 0 ? (
                                                        <Typography variant="caption" color="text.secondary">{r.phone_number ? '모름' : ''}</Typography>
                                                    ) : c.matches.map((m) => (
                                                        <Typography key={`${m.kind}-${m.id}`} variant="caption" display="block">
                                                            {WHO_LABEL[m.contact_label] || ''} · {personName(m)}{m.consultation_number ? ` #${m.consultation_number}` : ''}
                                                        </Typography>
                                                    ))}
                                                </TableCell>
                                                <TableCell>
                                                    {c?.merge_into ? (
                                                        <DuplicateChoice candidate={c} value={r.on_duplicate} onChange={(v) => setRow(r.key, { on_duplicate: v })} disabled={busy} timeUnknown={flow.pastDay && !r.missed_time} />
                                                    ) : (
                                                        <Typography variant="caption" color="text.secondary">—</Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                        {candidateNotes(c).map((n) => (
                                                            <Chip key={n} size="small" label={n} color={n.startsWith('확인') || n.startsWith('번호') ? 'warning' : 'default'} variant="outlined" />
                                                        ))}
                                                    </Box>
                                                    {compare && (
                                                        <Button
                                                            size="small"
                                                            sx={{ textTransform: 'none', mt: 0.5 }}
                                                            onClick={() => setRow(r.key, {
                                                                phone_number: formatPhone(compare.phone_number),
                                                                missed_time: compare.missed_time || r.missed_time,
                                                                missed_count: compare.missed_count || r.missed_count,
                                                            })}
                                                        >
                                                            두 번째 판독 사용: {formatPhone(compare.phone_number)} {compare.missed_time} ({compare.missed_count})
                                                        </Button>
                                                    )}
                                                    {errors[i] && <Typography variant="caption" color="error" display="block">{errors[i]}</Typography>}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        {undecided > 0 && (
                            <Alert severity="warning" sx={{ mt: 1 }}>
                                {multiDay ? '그 날 목록에 이미' : `${dayPhrase(flow.importDate, config?.today)} 이미`} 있는 번호가 {undecided}건 있습니다. 같은 번호 칸에서 합치기 / 새 행 / 무시를 고르면 저장할 수 있습니다.
                            </Alert>
                        )}
                        {review.length === 0 && (
                            <Typography color="text.secondary" sx={{ py: 2 }}>{multiDay ? '고른 날짜의' : dayWord} 부재중 전화를 찾지 못했습니다. 빠진 통화가 있으면 행을 추가하세요.</Typography>
                        )}
                        <Button startIcon={<AddIcon />} sx={{ mt: 1, textTransform: 'none' }} onClick={addRow}>
                            행 추가
                        </Button>
                    </>
                )}
            </DialogContent>

            <DialogActions>
                {step === 'confirm' && (
                    <>
                        <Button onClick={onClose}>아니요</Button>
                        <Button variant="contained" onClick={() => setStep('pick')}>예, 오늘 찍었습니다</Button>
                    </>
                )}
                {step === 'pick' && (
                    <>
                        <Button onClick={onClose}>취소</Button>
                        <Button variant="contained" onClick={flow.upload} disabled={!files.length}>올리기 ({files.length})</Button>
                    </>
                )}
                {step === 'reading' && <Button onClick={discardAndClose}>취소</Button>}
                {step === 'failed' && (
                    <>
                        <Button onClick={discardAndClose} disabled={busy}>취소</Button>
                        <Button variant="contained" onClick={flow.retry} disabled={busy}>다시 시도</Button>
                    </>
                )}
                {step === 'review' && (
                    <>
                        <Button onClick={discardAndClose} disabled={busy}>취소</Button>
                        <Tooltip title={undecided ? '같은 번호에서 하나를 골라 주세요' : ''}>
                            <span>
                                <Button variant="contained" onClick={save} disabled={!flow.canSave}>
                                    {busy ? '저장 중…' : `저장 (${flow.included})`}
                                </Button>
                            </span>
                        </Tooltip>
                    </>
                )}
            </DialogActions>

            <Dialog open={Boolean(zoom)} onClose={() => setZoom(null)} maxWidth="md" disableEnforceFocus>
                {zoom && <img src={zoom} alt="스크린샷" style={{ maxWidth: '100%', maxHeight: '90vh', display: 'block' }} onClick={() => setZoom(null)} />}
            </Dialog>
        </Dialog>
    );
};

export default ImportDialog;
